import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { EventConfigResponseData } from 'src/app/@data-services/event-config/event-config.data-model';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import { MixpanelService } from 'src/app/@services/mixpanel.service';
import { LanguageStore } from 'src/app/@store/language-store';
import { applyDocumentTitle } from 'src/app/@utils/apply-document-title';
import { applyTheme } from 'src/app/@utils/apply-theme';
import { environment } from 'src/environments/environment';
import { getEuEndpointsForHostname } from './eu-event-routing.config';
import {
  eventConfigRequestBody,
  isSubpathMode,
  resolveEventDomain,
} from 'src/app/@utils/base-path';

/** sessionStorage can throw on access (storage disabled); the resolver handles null. */
function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function appEventConfigInitializer() {
  const http = inject(HttpClient);

  // Subdomain mode: identical to before (hostname / localhostDomain).
  // Subpath mode (/event/ on the console origin): explicit ?event= param.
  const eventDomain = resolveEventDomain({
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    search: window.location.search,
    localhostDomain: environment.localhostDomain,
    // sessionStorage: per-tab, never shared with the console's localStorage tokens
    storage: safeSessionStorage(),
  });
  if (eventDomain === null) {
    return (): Promise<void> =>
      Promise.reject(
        new Error(
          'Insights under a subpath (/event/) needs an ?event=<event> parameter'
        )
      );
  }

  // Apply EU endpoint overrides if the current event is hosted in EU.
  // In subdomain mode eventDomain === hostname (unchanged); in subpath mode the
  // console host says nothing about the event, so route on the event domain.
  const euEndpoints = getEuEndpointsForHostname(
    window.location.hostname === 'localhost' &&
      !isSubpathMode(window.location.pathname)
      ? window.location.hostname
      : eventDomain,
    environment.euStage
  );
  if (euEndpoints) {
    environment.apiUrl = euEndpoints.apiUrl;
    environment.wsUrl = euEndpoints.wsUrl;
    environment.X_API_KEY = euEndpoints.xApiKey;
  }

  const apiUrl = environment.apiUrl;
  const eventConfigStateService = inject(EventConfigStateService);
  const languageStore = inject(LanguageStore);
  const mixpanelService = inject(MixpanelService);

  return (): Promise<void> =>
    firstValueFrom(
      http
        .post<EventConfigResponseData>(
          apiUrl + '/r1/getEventConfig',
          eventConfigRequestBody(eventDomain, window.location.pathname)
        )
        .pipe(
          map((response) => {
            eventConfigStateService.setEventIdentifier(
              response.data.EventIdentifier
            );
            eventConfigStateService.setEventConfigData(response.data);
            eventConfigStateService.setSponsors(response.data.Sponsors);
            eventConfigStateService.setFeatureConfigData(
              response.data.Features
            );
            languageStore.setAvailableLanguages(
              response.data.SupportedLanguages
            );
            languageStore.setOriginalLanguageCode(
              response.data.OriginalLanguageCode
            );
            applyTheme(response.data.Themes);
            applyDocumentTitle(response.data.Information.EventNameDisplay);
            mixpanelService.registerEvent(
              response.data.EventIdentifier,
              eventDomain
            );
          })
        )
    );
}
